import React, { useCallback, useState } from 'react';
import { cn } from '../../utils/cn';
import { Asset, getAssetIconByExtension, formatFileSize, formatRelativeTime } from '../../../core/types/asset.types';
import { useProjectPanelStore } from '../../store/projectPanelStore';
import { useAssetPreviewStore } from '../../store/assetPreviewStore';

interface AssetRowProps {
  asset: Asset;
  isSelected: boolean;
  onSelect: (id: string, multiSelect?: boolean) => void;
}

const TYPE_LABELS: Record<string, string> = {
  sprite: 'Sprite', 'audio-music': 'Music', 'audio-sfx': 'SFX', 'script-ts': 'TypeScript', 'script-js': 'JavaScript', prefab: 'Prefab', scene: 'Scene', font: 'Font',
};

export const AssetRow: React.FC<AssetRowProps> = ({ asset, isSelected, onSelect }) => {
  const { setDraggedAsset, showContextMenu } = useProjectPanelStore();
  const [isDragging, setIsDragging] = useState(false);

  const handleClick = useCallback((e: React.MouseEvent) => onSelect(asset.id, e.ctrlKey || e.metaKey), [asset.id, onSelect]);
  const handleDoubleClick = useCallback(() => {
    const { openPreview } = useAssetPreviewStore.getState();
    switch (asset.type) {
      case 'sprite':
      case 'video':
      case 'audio-music':
      case 'audio-sfx':
      case 'prefab':
        openPreview(asset);
        break;
      case 'scene':
        import('../../store/sceneStore').then(({ useSceneStore }) => {
          useSceneStore.getState().loadScene(asset.path).catch((error) => {
            console.error('Failed to load scene:', error);
          });
        });
        break;
      default:
        openPreview(asset);
    }
  }, [asset]);
  const handleContextMenu = useCallback((e: React.MouseEvent) => { e.preventDefault(); onSelect(asset.id); showContextMenu(e.clientX, e.clientY, asset.id); }, [asset.id, onSelect, showContextMenu]);
  const handleDragStart = useCallback((e: React.DragEvent) => { e.dataTransfer.setData('text/plain', asset.id); setDraggedAsset(asset.id); setIsDragging(true); }, [asset.id, setDraggedAsset]);
  const handleDragEnd = useCallback(() => { setDraggedAsset(null); setIsDragging(false); }, [setDraggedAsset]);

  return (
    <div
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={cn('flex items-center px-2 py-1.5 border-b border-zinc-800 text-xs cursor-pointer hover:bg-zinc-700/50', isSelected && 'bg-blue-600/50', isDragging && 'opacity-50')}
    >
      <div className="flex-1 min-w-[200px] flex items-center gap-2 truncate">
        <span className="text-sm">{getAssetIconByExtension(asset)}</span>
        <span className={cn('truncate', isSelected ? 'text-white' : 'text-zinc-300')}>{asset.name}</span>
      </div>
      <div className={cn('w-24', isSelected ? 'text-white/70' : 'text-zinc-500')}>{TYPE_LABELS[asset.type] || asset.type}</div>
      <div className={cn('w-20 text-right', isSelected ? 'text-white/70' : 'text-zinc-500')}>{formatFileSize(asset.metadata?.fileSize || 0)}</div>
      <div className={cn('w-24 text-right', isSelected ? 'text-white/70' : 'text-zinc-500')}>{formatRelativeTime(new Date(asset.modifiedAt))}</div>
    </div>
  );
};
